import { useEffect, useMemo } from 'react';
import { useStore } from '../../context';
import { useTextSelect } from '../../hooks';
import {
  FileViewContent,
  FileViewWrapper,
  HtmlContent,
  TextContent,
} from './styles';
import { useEmail } from './use-email';
import { useTextHighlight } from '../../hooks/use-text-highlight';

export const EmailView = () => {
  const file = useStore(({ file: f }) => f.getFile());
  const highlights = useStore(({ file: f }) => f.highlights);
  const textContent = useStore(({ file: f }) => f.textContent);
  const { readEmail, isLoading, emailContent } = useEmail();
  const { getHighlightedContent } = useTextHighlight();

  useTextSelect();

  useEffect(() => {
    if (file) {
      readEmail();
    }
  }, [file]);

  const highlightedHtml = useMemo(() => {
    if (!emailContent.html || !highlights || highlights.length === 0) return emailContent.html;

    const targetOccurrenceMap = new Map<string, number>();
    const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const countOccurrences = (haystack: string, needle: string) => {
      if (!needle) return 0;
      let count = 0;
      let from = 0;
      const constantCond = true;
      while (constantCond) {
        const idx = haystack.indexOf(needle, from);
        if (idx === -1) break;
        count++;
        from = idx + needle.length;
      }
      return count;
    };

    if (textContent) {
      for (const h of highlights) {
        if (
          h.head >= 0 &&
          h.tail <= textContent.length &&
          h.head <= h.tail
        ) {
          const before = textContent.slice(0, h.head);
          const occ = countOccurrences(before, h.txt);
          const key = `${h.head}-${h.tail}-${h.color}-${h.txt}`;
          targetOccurrenceMap.set(key, occ);
        }
      }
      try {
        // Debug: log computed targets
        const debugTargets: Record<string, number> = {};
        targetOccurrenceMap.forEach((v, k) => (debugTargets[k] = v));
      } catch {
        // noop
      }
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(emailContent.html, 'text/html');

    const currentCountMap = new Map<string, number>();
    const wrappedKeys = new Set<string>();

    const wrapOccurrenceInTextNode = (
      node: Text,
      needle: string,
      color: string,
      shouldWrapThisOccurrence: (currentCount: number) => boolean,
      currentCount: number,
      key: string
    ): { replaced: boolean; nextTextNode: Text | null } => {
      const text = node.nodeValue || '';
      const index = text.indexOf(needle);
      if (index === -1) return { replaced: false, nextTextNode: null };

      const beforeText = text.slice(0, index);
      const matchText = text.slice(index, index + needle.length);
      const afterText = text.slice(index + needle.length);

      const parent = node.parentNode;
      if (!parent) return { replaced: false, nextTextNode: null };

      const frag = doc.createDocumentFragment();
      if (beforeText) frag.appendChild(doc.createTextNode(beforeText));

      const shouldWrap = shouldWrapThisOccurrence(currentCount);

      if (shouldWrap) {
        const mark = doc.createElement('mark');
        mark.setAttribute('style', `background-color: ${color};`);
        mark.textContent = matchText;
        frag.appendChild(mark);
        wrappedKeys.add(key);
      } else {
        frag.appendChild(doc.createTextNode(matchText));
      }

      const afterNode = afterText ? doc.createTextNode(afterText) : null;
      if (afterNode) frag.appendChild(afterNode);

      parent.replaceChild(frag, node);
      return { replaced: true, nextTextNode: afterNode };
    };

    const isHiddenElement = (el: Element) => {
      const style = (el.getAttribute('style') || '').toLowerCase();
      if (style.includes('display:none') || style.includes('visibility:hidden')) return true;
      if (el.hasAttribute('hidden')) return true;
      if (el.getAttribute('aria-hidden') === 'true') return true;
      const tag = el.tagName.toLowerCase();
      if (tag === 'script' || tag === 'style' || tag === 'template' || tag === 'noscript') return true;
      return false;
    };

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;

        for (const h of highlights) {
          if (!h.txt) continue;
          const key = `${h.head}-${h.tail}-${h.color}-${h.txt}`;
          if (wrappedKeys.has(key)) continue;
          const target = targetOccurrenceMap.get(key) ?? 0;
          let nodePtr: Text | null = textNode;
          while (nodePtr) {
            const current = currentCountMap.get(key) ?? 0;
            const { replaced, nextTextNode } = wrapOccurrenceInTextNode(
              nodePtr,
              h.txt,
              h.color,
              (cnt) => cnt === target,
              current,
              key
            );
            if (!replaced) break;
            currentCountMap.set(key, current + 1);
            nodePtr = nextTextNode;
            if (wrappedKeys.has(key)) break;
          }
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (isHiddenElement(el)) return;
        const children = Array.from(el.childNodes);
        for (const child of children) {
          processNode(child);
        }
      }
    };

    // Skip per-node traversal; rely on flattened text Range-based wrapping below

    // Fallback: if target occurrence wasn't wrapped due to cross-node split, use Range over flattened text
    const flattenTextNodes = (root: Node) => {
      const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes: { node: Text; text: string; start: number; end: number }[] = [];
      let offset = 0;
      let current: Node | null = walker.nextNode();
      while (current) {
        const t = (current as Text).nodeValue || '';
        const parentEl = current.parentElement;
        if (t.length > 0 && parentEl && !isHiddenElement(parentEl)) {
          nodes.push({ node: current as Text, text: t, start: offset, end: offset + t.length });
          offset += t.length;
        }
        current = walker.nextNode();
      }
      return { nodes, totalText: nodes.map(n => n.text).join('') };
    };

    const findNthOccurrenceInString = (haystack: string, needle: string, n: number) => {
      if (!needle) return -1;
      let idx = -1;
      let from = 0;
      for (let i = 0; i <= n; i++) {
        idx = haystack.indexOf(needle, from);
        if (idx === -1) return -1;
        from = idx + needle.length;
      }
      return idx;
    };

    try {
      let { nodes, totalText } = flattenTextNodes(doc.body);
      const rebuildNodes = () => {
        const res = flattenTextNodes(doc.body);
        nodes = res.nodes;
        totalText = res.totalText;
      };

      const wrapAcrossNodes = (start: number, end: number, color: string) => {
        for (let i = 0; i < nodes.length; i++) {
          const info = nodes[i];
          if (info.end <= start) continue;
          if (info.start >= end) break;
          const localStart = Math.max(0, start - info.start);
          const localEnd = Math.min(info.text.length, end - info.start);
          if (localEnd <= localStart) continue;

          const node = info.node;
          const text = node.nodeValue || '';
          const before = text.slice(0, localStart);
          const middle = text.slice(localStart, localEnd);
          const after = text.slice(localEnd);
          const parent = node.parentNode;
          if (!parent) continue;

          const frag = doc.createDocumentFragment();
          if (before) frag.appendChild(doc.createTextNode(before));
          const m = doc.createElement('mark');
          m.setAttribute('style', `background-color: ${color};`);
          m.textContent = middle;
          frag.appendChild(m);
          if (after) frag.appendChild(doc.createTextNode(after));
          parent.replaceChild(frag, node);
          rebuildNodes();
        }
      };

      for (const h of highlights) {
        const key = `${h.head}-${h.tail}-${h.color}-${h.txt}`;
        if (wrappedKeys.has(key)) continue;
        const target = targetOccurrenceMap.get(key) ?? 0;
        const globalStart = findNthOccurrenceInString(totalText, h.txt, target);
        if (globalStart === -1) {
          continue;
        }
        const globalEnd = globalStart + h.txt.length;
        wrapAcrossNodes(globalStart, globalEnd, h.color);
        wrappedKeys.add(key);
      }
    } catch (e) {
      //
    }
    return doc.body.innerHTML;
  }, [emailContent.html, highlights, textContent]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <FileViewWrapper>
      <FileViewContent>
        {emailContent.html ? (
          <HtmlContent
            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
          />
        ) : (
          <TextContent
            dangerouslySetInnerHTML={{
              __html: getHighlightedContent(emailContent.text, highlights, 0),
            }}
          />
        )}
      </FileViewContent>
    </FileViewWrapper>
  );
};


